import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  LinearScale,
  CategoryScale, PointElement,
  Tooltip,
  Filler
} from "chart.js";
import { useDispatch, useSelector } from "react-redux";
import { fetchGraphData } from "../../../../store/reducers/graphDataSlice";


ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Filler
);

const AnalyticsChart = ({ userId, organizationId, style }) => {
  const [tab, setTab] = useState("weekly");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const { weekly, monthly, loading, error } = useSelector(
    (state) => state.graphData || {
      weekly: { labels: [], points: [] },
      monthly: { labels: [], points: [] },
      loading: false,
      error: null,
    }
  );

  // Fetch graph data on mount or when user/org changes
  useEffect(() => {
    if (userId && organizationId) {
      dispatch(fetchGraphData({ userId, organizationId }));
    }
  }, [userId, organizationId, dispatch]);

  const selectedData = tab === "weekly" ? weekly : monthly;

  const data = {
    labels: selectedData.labels || [],
    datasets: [
      {
        label: "Points",
        data: selectedData.points || [],
        fill: true,
        backgroundColor: "rgba(255, 59, 63, 0.2)",
        borderColor: "rgba(246, 96, 95, 1)",
        tension: 0.4,
        pointRadius: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          maxTicksLimit: isMobile ? 7 : 14,
          color: "black",
          font: {
            weight: "bold",
            size: 10,
          },
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `${value} P`,
          color: "black",
          font: {
            weight: "bold",
          },
          stepSize: selectedData?.points?.length === 1 ? 1 : undefined,
          precision: 0,
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.y} P`,
        },
      },
      legend: {
        display: false,
      },
    },
  };

  return (
    <Box style={style}>
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
        Analytics
      </Typography>

      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          p: isMobile ? 1 : 2,
          background: "#fff0f0",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Tabs
            value={tab}
            onChange={(e, newValue) => setTab(newValue)}
            textColor="inherit"
            TabIndicatorProps={{
              style: {
                backgroundColor: "rgba(246, 96, 95, 1)",
              },
            }}
            sx={{
              "& .MuiTab-root": {
                fontWeight: 600,
                textTransform: "none",
              },
              "& .Mui-selected": {
                color: "rgba(246, 96, 95, 1) !important",
              },
            }}
          >
            <Tab value="weekly" label="Weekly" />
            <Tab value="monthly" label="Monthly" />
          </Tabs>
        </Box>

        {loading ? (
          <Typography sx={{ mt: 2 }}>Loading...</Typography>
        ) : error ? (
          <Typography sx={{ mt: 2, color: "red" }}>{error}</Typography>
        ) : (
          <Box sx={{ height: "40vh", mt: 2 }}>
            <Line data={data} options={options} />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AnalyticsChart;
